import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Dropdown, MessageBarActions, MessageBarBody, MessageBarTitle, Option } from '@fluentui/react-components';
import { Open16Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';

import { GuideDrawer } from '../../../theme/components/GuideDrawer';
import { GuideField, GuideFieldGrid } from '../../../theme/components/GuideField';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import { GuideTable, GuideTh, guideRowClassName } from '../../../theme/components/GuideTable';
import { GuidePagination } from '../../../theme/components/GuidePagination';
import { GuideEmptyState } from '../../../theme/components/GuideEmptyState';
import { GuideSkeleton } from '../../../theme/components/GuideSkeleton';
import { GuideBannerAction, GuideLinkButton } from '../../../theme/components/GuideButtons';
import { StatusPill } from '../../../theme/components/StatusPill';
import { ProtocolPicker } from './ProtocolPicker';
import { alarmReasonLabelKey, formatDateTime, protocolConsumptionLink } from '../../../functions/ProviderConsumptions.Functions';
import { formatImportDate, getApiErrorMessage } from '../../../functions/ProviderConsumptionImport.Functions';
import {
  ProviderSyncAlarm,
  ProviderSyncAlarmsFilter,
  ProviderSyncAlarmsPage,
  ProviderSyncRun,
  SETTLEMENT_ALARM_REASONS,
} from '../../../models/Entities/ProviderConsumptions/ProviderConsumptions';
import { Protocol } from '../../../models/Entities/Protocols/Protocol';

export const SYNC_ALARMS_PAGE_SIZE = 50;
const ALL = '__all__';

export interface ProviderSyncAlarmsDrawerProps {
  open: boolean;
  onDismiss: () => void;
  /** Corrida cuyas alarmas se listan. */
  run: ProviderSyncRun | undefined;
  protocols: Protocol[];
  LoadAlarms: (runId: string, filter: ProviderSyncAlarmsFilter) => Promise<ProviderSyncAlarmsPage>;
  /** Abre el detalle del consumo de protocolo de la alarma. */
  onOpenConsumption: (protocolId: number, consumptionId: string) => void;
}

/** Alarmas de una corrida de sincronización: paginadas (50), con filtros por motivo y protocolo. */
export const ProviderSyncAlarmsDrawer = (props: ProviderSyncAlarmsDrawerProps) => {
  const { t } = useTranslation();
  const tr = (path: string, options?: Record<string, unknown>) => t(`providerConsumptions.alarms.${path}`, options);
  const runId = props.run?.id;

  const [reason, setReason] = useState<number | undefined>(undefined);
  const [protocolId, setProtocolId] = useState<number | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<ProviderSyncAlarmsPage | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(undefined);
  // Sólo se aplica la respuesta del último pedido (cambios de filtro rápidos o reapertura).
  const requestRef = useRef(0);

  useEffect(() => {
    if (!props.open) return;
    setReason(undefined);
    setProtocolId(undefined);
    setPage(1);
    setResult(undefined);
  }, [props.open, runId]);

  const load = useCallback(async () => {
    if (!props.open || !runId) return;
    const requestId = ++requestRef.current;
    setLoading(true);
    setError(undefined);
    try {
      const next = await props.LoadAlarms(runId, { reason, protocolId, pageNumber: page, pageSize: SYNC_ALARMS_PAGE_SIZE });
      if (requestId === requestRef.current) setResult(next);
    } catch (failure: unknown) {
      if (requestId === requestRef.current) setError(failure);
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, runId, reason, protocolId, page]);

  useEffect(() => {
    load();
  }, [load]);

  const items = result?.items ?? [];
  const filtered = reason !== undefined || protocolId !== undefined;

  const renderRow = (alarm: ProviderSyncAlarm) => {
    const link = protocolConsumptionLink({ protocolId: alarm.protocolId, sourceProtocolConsumptionId: alarm.sourceProtocolConsumptionId });
    return (
      <tr key={alarm.id} className={guideRowClassName({ pending: true })} data-testid={`pcs-alarm-${alarm.id}`}>
        <td className="ichtys-num pcs-nowrap">{formatImportDate(alarm.serviceDueDate)}</td>
        <td>{alarm.serviceName || '—'}</td>
        <td>{alarm.protocolId ?? '—'}</td>
        <td>
          {alarm.patientNumberInProtocol || '—'}
          {alarm.medicalRecordNumber && <span className="pcs-secondary">{alarm.medicalRecordNumber}</span>}
        </td>
        <td>
          <StatusPill tone="warning">{t(alarmReasonLabelKey(alarm.reason))}</StatusPill>
          {alarm.reasonDescription && <span className="pcs-secondary">{alarm.reasonDescription}</span>}
          {alarm.detail && alarm.detail !== alarm.reasonDescription && <span className="pcs-secondary">{alarm.detail}</span>}
        </td>
        <td>
          {link ? (
            <GuideLinkButton
              size="small"
              icon={<Open16Regular />}
              onClick={() => props.onOpenConsumption(link.protocolId, link.consumptionId)}
              data-testid={`pcs-alarm-open-${alarm.id}`}
            >
              {tr('viewConsumption')}
            </GuideLinkButton>
          ) : (
            <span className="pcs-muted">—</span>
          )}
        </td>
      </tr>
    );
  };

  const renderBody = () => {
    if (loading && !result) return <GuideSkeleton rows={4} label={tr('loading')} data-testid="pcs-alarms-loading" />;
    if (error !== undefined && !result) return null;
    if (items.length === 0) {
      return filtered ? (
        <GuideEmptyState title={tr('emptyFilteredTitle')} text={tr('emptyFilteredText')} data-testid="pcs-alarms-empty" />
      ) : (
        <GuideEmptyState title={tr('emptyTitle')} data-testid="pcs-alarms-empty" />
      );
    }
    return (
      <GuideTable
        ariaLabel={tr('title')}
        data-testid="pcs-alarms-table"
       
        scrollTestId="pcs-alarms-table-scroll"
        footer={<GuidePagination page={page} pageSize={SYNC_ALARMS_PAGE_SIZE} total={result?.totalRecords ?? items.length} onPageChange={setPage} />}
      >
        <thead>
          <tr>
            <GuideTh>{tr('columns.date')}</GuideTh>
            <GuideTh>{tr('columns.service')}</GuideTh>
            <GuideTh>{tr('columns.protocol')}</GuideTh>
            <GuideTh>{tr('columns.patient')}</GuideTh>
            <GuideTh>{tr('columns.reason')}</GuideTh>
            <GuideTh>{tr('columns.consumption')}</GuideTh>
          </tr>
        </thead>
        <tbody>{items.map(renderRow)}</tbody>
      </GuideTable>
    );
  };

  return (
    <GuideDrawer
      open={props.open}
      size="large"
      onDismiss={props.onDismiss}
      eyebrow={props.run ? tr('eyebrow', { date: formatDateTime(props.run.requestedAt) }) : undefined}
      title={tr('title')}
      subtitle={tr('subtitle', { count: result?.totalRecords ?? props.run?.alarmCount ?? 0 })}
      testId="pcs-alarms-drawer"
    >
      {error !== undefined && (
        <GuideMessageBar intent="error" layout="multiline" data-testid="pcs-alarms-error">
          <MessageBarBody>
            <MessageBarTitle>{tr('loadError')}</MessageBarTitle>
            {getApiErrorMessage(error)}
          </MessageBarBody>
          <MessageBarActions>
            <GuideBannerAction onClick={load}>{t('providerConsumptions.errors.retry')}</GuideBannerAction>
          </MessageBarActions>
        </GuideMessageBar>
      )}
      <GuideFieldGrid>
        <GuideField label={tr('filters.reason')}>
          <Dropdown
            value={reason !== undefined ? t(alarmReasonLabelKey(reason)) : tr('filters.allReasons')}
            selectedOptions={[reason !== undefined ? String(reason) : ALL]}
            onOptionSelect={(_event, data) => {
              setReason(data.optionValue && data.optionValue !== ALL ? Number(data.optionValue) : undefined);
              setPage(1);
            }}
            data-testid="pcs-alarms-reason"
          >
            <Option value={ALL}>{tr('filters.allReasons')}</Option>
            {SETTLEMENT_ALARM_REASONS.map((value) => (
              <Option key={value} value={String(value)} text={String(t(alarmReasonLabelKey(value)))}>
                {String(t(alarmReasonLabelKey(value)))}
              </Option>
            ))}
          </Dropdown>
        </GuideField>
        <GuideField label={tr('filters.protocol')}>
          <ProtocolPicker
            protocols={props.protocols}
            value={protocolId}
            onChange={(value) => {
              setProtocolId(value);
              setPage(1);
            }}
            placeholder={t('providerConsumptions.filters.protocolPlaceholder')}
            emptyText={t('providerConsumptions.filters.noProtocols')}
            data-testid="pcs-alarms-protocol"
          />
        </GuideField>
      </GuideFieldGrid>
      {renderBody()}
    </GuideDrawer>
  );
};

export default ProviderSyncAlarmsDrawer;
